import {
  AbodeAggregateMarketConditions,
  AccountEmail,
  AllFTUXes,
  AloftDateJSON,
  AloftPriceJSON,
  AloftTimeJSON,
  Commentary,
  CraftsmanAdjustmentsCalcVersion,
  CraftsmanCostDatasetVersion,
  CraftsmanReplacementCostCalcVersion,
  DepreciationSettingsV2JSON,
  EscalationType,
  FeatureDecisions,
  InspectionData,
  MLSID,
  MainPropertyType,
  MarketAdjustments,
  McKissockAccountData,
  OrderFeature,
  PairedCostSettingsJSON,
  PriorSalesEntities,
  PropertyData,
  Reconciliation,
  RevisionData,
  SalesComparisonApproachType,
  SubjectData,
  Tkv2OrderAttachmentBody,
  UserType,
} from '@aloft/toolkit-shared';
import {
  ActionReducerMapBuilder,
  EntityState,
  Reducer,
  ThunkDispatch,
  UnknownAction,
  combineReducers,
} from '@reduxjs/toolkit';

import { PropertyGridFieldName, PropertyGridType } from '../components/PropertyGrid/types.ts';
import { Tkv2ReferenceBarProperty } from '../controller/types.ts';
import { ReferenceViewID } from '../modules/referenceView/index.ts';
import { DataSheet } from '../modules/referenceView/types.ts';

export type RootReducer = ReturnType<
  typeof combineReducers<{
    [K in keyof RootState]: Reducer<RootState[K]>;
  }>
>;

export type RootState = {
  account: AccountState;
  alerts: AlertsState;
  order: OrderState;
  commentary: CommentaryState;
  priorSales: PriorSalesState;
  reconciliation: ReconciliationState;
  propertyData: EntityState<PropertyDataState, string>;
  referenceBarUI: ReferenceBarUIState;
  ui: UIState;
  features: FeaturesState;
};

export type AppDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

type WithSyncState<T> = T & { syncState?: SyncState };

// Sync State
export type SyncState = {
  editRevision: number;
  serverRevision: number;
  status: 'success' | 'error';
};

export type SyncResult =
  | {
      status: 'success';
      serverRevision: number;
    }
  | {
      status: 'error';
    };

// Account
export type AccountState = {
  id?: string;
  email?: AccountEmail;
  firstName?: string;
  lastName?: string;
  phone?: string;
  mlsIDs: MLSID[];
  isOnboardingComplete?: boolean;
  ftux: AllFTUXes;
  mcKissockData?: McKissockAccountData;
  signatureBlobId?: string;
  userType?: UserType;
  isAdmin?: boolean;
};

// Order
export type CompSaveQueueItem = {
  comps: PropertyData[];
  sequenceTarget: 'compSequence' | 'rentalCompSequence';
};

export type OrderState = {
  id: string;
  compSequence: string[];
  rentalCompSequence: string[];
  effectiveDate?: AloftDateJSON;
  compSaveQueue: CompSaveQueueItem[];
  deletedComps: string[];
  compSequenceSync?: SyncState;
  rentalCompSequenceSync?: SyncState;
  subjectLoaded: boolean;
  compsLoaded: boolean;
  orderFeatures: OrderFeature[];
  orderFeaturesSync?: SyncState;
  orderFeaturesLoaded: boolean;
  marketAdjustments: MarketAdjustments;
  marketAdjustmentsSync?: SyncState;
  marketAdjustmentsLoaded: boolean;
  marketConditionsLoaded: boolean;
  propertyType?: MainPropertyType;
  adjustmentSettings: PairedCostSettingsJSON;
  adjustmentSettingsSync?: SyncState & { saveAsDefault?: boolean };
  adjustmentSettingsLoaded: boolean;
  datasetVersion: CraftsmanCostDatasetVersion;
  calculatorVersion: CraftsmanReplacementCostCalcVersion;
  adjustmentVersion: CraftsmanAdjustmentsCalcVersion;
  depreciationSettings: DepreciationSettingsV2JSON;
  depreciationSettingsSync?: SyncState & { saveAsDefault?: boolean };
  depreciationSettingsLoaded: boolean;
  costMultiplier?: number;
  detailsSync?: SyncState;
  isArchived: boolean;
  isDeleted: boolean;
  isTraining: boolean;
  isMock: boolean;
  isInspectifyOrder: boolean;
  hasArvOption: boolean;
  hasAvmOption: boolean;
  hasRentalConfiguration: boolean;
  orderNumber?: string;
  inspectorType?: string;
  clientName?: string;
  borrowerName?: string;
  appraisalType?: string;
  inspectionDate?: AloftDateJSON;
  intendedUsers?: string;
  intendedUse?: string;
  appraiserFee?: AloftPriceJSON;
  appraisifyFee?: AloftPriceJSON;
  appraiserInstructions?: string;
  licenseState?: string;
  licenseNumber?: string;
  licenseExpiration?: AloftDateJSON;
  licenseType?: string;
  signedAt?: AloftTimeJSON;
  inspectionData?: InspectionData;
  previousDesktopOrderID?: string;
  isRevision?: boolean;
  revisionData?: RevisionData;
  previousRevisionsData?: RevisionData[];
  inspectionId?: string;
  marketAggregateData?: AbodeAggregateMarketConditions[];
  appraisifyName?: string;
  appraisifyAddress?: string;
  compTypes: {
    [key: string]: 'arv' | 'aiv' | undefined;
  };
  startedAt?: AloftTimeJSON;
  appraisalReportDisclaimer?: string;
  isEscalated: boolean;
  escalationType?: EscalationType;
  salesComparisonApproach: SalesComparisonApproachType;
  includeCostApproach: boolean;
  createdAt: AloftTimeJSON;
};

// Prior Sales
export type PriorSalesState = WithSyncState<PriorSalesEntities>;

export type PropertyDataSliceExtraReducer = (
  builder: ActionReducerMapBuilder<EntityState<PropertyDataState, string>>,
) => void;

export type SubjectPropertyDataState = WithSyncState<{
  id: 'subject';
  subjectData: SubjectData;
}>;

export type CompPropertyDataState = WithSyncState<{
  id: string;
  compData: PropertyData;
}>;

export type PropertyDataState = SubjectPropertyDataState | CompPropertyDataState;

// UI
export type DeviceWidth = 'phone' | 'tablet' | 'desktop' | 'unconstrained';

export type ActiveTour = 'analysisTour';
export const AIV_ARV_TABS = ['all', 'aiv', 'arv'] as const;
export type AivArvActiveTab = (typeof AIV_ARV_TABS)[number];

export type UIState = {
  analysisTourIndex: number;
  activeTour: ActiveTour | null;
  activeDepreciateSeparatelyID: string;
  deviceWidth: DeviceWidth;
  isEditPropertyDetailsModalOpen: boolean;
  objectiveEditMode: string;
  propertyGridExpandedPropertyIds: Partial<Record<PropertyGridType, string>>;
  featureGridConflictedFields: {
    [key: string]: { fieldName: PropertyGridFieldName; propertyId: string };
  };
  selectedPriorSalesPropertyId: string;
  aivArvActiveTab: AivArvActiveTab;
  escalationBypassed: boolean;
};

export type ReferenceBarUIState = {
  referenceViewOpen: boolean;
  mobileReferenceMenuOpen: boolean;
  properties: Tkv2ReferenceBarProperty[];
  selectedDataSheet: DataSheet | null;

  // Since this can potentially come from router params, we
  // should never select it directly; this is just activeID,
  // but we should use useActiveReferenceBarID to get it.
  __doNotUseDirectly__ActiveID: ReferenceViewID;
};

// Features
export type FeaturesState = { [k in keyof FeatureDecisions]: boolean };

// Commentary
export type CommentaryState = WithSyncState<Commentary>;

// Reconciliation
export type ReconciliationState = WithSyncState<Reconciliation>;

// Alerts
export type AlertsState = WithSyncState<Tkv2OrderAttachmentBody<'alerts'>>;
